/**
 * Select Component
 *
 * Labeled dropdown select field.
 *
 * @package PresszoneComments
 */

import { el } from '../utils/dom.js';
import tooltipManager from './Tooltip.js';

/**
 * Render a select dropdown.
 *
 * @param {string} key - State key
 * @param {string} label - Field label
 * @param {string} help - Tooltip help text
 * @param {Array} options - Array of { value, label, default? } objects
 * @param {Object} state - State object containing value
 * @param {Function} onUpdate - Callback when value changes
 * @param {Function} onChange - Additional onChange callback
 * @param {Object} props - Additional properties (styling, padding, attrs, etc.)
 */
export function Select(key, label, help, options, state, onUpdate, onChange = null, props = {}) {
    const { styling, padding, showBorders = true, style, ...inputProps } = props;

    const classes = ['presszone-comments-select--settings'];
    if (styling) classes.push(`presszone-comments-select--styling-${styling}`);
    if (padding) classes.push(`presszone-comments-select--padding-${padding}`);
    if (showBorders === false) classes.push('presszone-comments-select--no-borders');

    return el('div', { class: 'presszone-comments-form-group', style: style },
        el('label', { class: 'presszone-comments-label--settings', for: key },
            label,
            help ? el('button', {
                type: 'button',
                class: 'presszone-comments-tooltip-trigger',
                'aria-label': `More information about ${label}`,
                onmouseenter: (e) => {
                    if (!('ontouchstart' in window)) {  // Desktop only
                        tooltipManager.show(e.target, help);
                    }
                },
                onmouseleave: (e) => {
                    if (!('ontouchstart' in window)) {  // Desktop only
                        tooltipManager.hide();
                    }
                },
                onfocus: (e) => {
                    if (!('ontouchstart' in window)) {  // Desktop only
                        tooltipManager.show(e.target, help);
                    }
                },
                onblur: (e) => {
                    if (!('ontouchstart' in window)) {  // Desktop only
                        tooltipManager.hide();
                    }
                },
                onkeydown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if ('ontouchstart' in window) {
                            // Mobile behavior via keyboard
                            if (tooltipManager.activeTooltip === e.target) {
                                tooltipManager.hide();
                            } else {
                                tooltipManager.show(e.target, help);
                            }
                        } else {
                            // Desktop: show tooltip
                            tooltipManager.show(e.target, help);
                        }
                    } else if (e.key === 'Escape') {
                        tooltipManager.hide();
                    }
                },
                onclick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Mobile: toggle tooltip on click
                    if ('ontouchstart' in window) {
                        if (tooltipManager.activeTooltip === e.target) {
                            tooltipManager.hide();
                        } else {
                            tooltipManager.show(e.target, help);
                        }
                    }
                }
            }, 'ⓘ') : null
        ),
        el('select', {
            id: key,
            class: classes.join(' '),
            onchange: (e) => {
                state[key] = e.target.value;
                if (onUpdate) onUpdate(key, e.target.value);
                if (onChange) onChange(e.target.value);
            },
            ...inputProps
        }, ...options.map(opt =>
            el('option', { value: opt.value, selected: state[key] === opt.value || (!state[key] && opt.default) }, opt.label)
        ))
    );
}

export default Select;
